import React from 'react'
import { noop } from 'lodash-es'
import styled from '@emotion/styled'
import moment from 'moment'
import { CollateralBasket, CollateralBasketType, Market, Product, ValidationIssue } from 'api'
import { DialogRow, DialogField, TextInputField, NumericInputField } from 'view/components/inputs'
import { ToggleField } from 'view/components/toggle'
import { CollateralBasketPanelProps, getCustomIsinIssues } from './CollateralBasketDialog'
import {
  TradingTypeSelectboxField,
  CollateralBasketProductSectorSelectboxField,
  CollateralBasketTypeSelectboxField
} from 'view/shared/entity'
import { TradingDatePickerField } from 'view/shared/entity'
import { Translation, Locale } from 'locale'
import * as constants from 'view/styles/constants'
import { useUpdateHandler } from 'view/hooks'

const NumericInputWithUnit = styled.div`
  display: flex;
`

const NumericInputUnit = styled.div`
  margin-left: 8px;
  margin-top: 15px;
`

interface CollateralBasketOverviewPanelProps extends CollateralBasketPanelProps {
  collateralBasketProduct: Product | null
  getProductSectorIdsForType: (basketType: CollateralBasketType) => string[]
  validationIssues: ValidationIssue[] | null
  isNewCollateralBasket: boolean
}

const EMPTY_VALIDATION_ISSUES: ValidationIssue[] = []

export const CollateralBasketOverviewPanel: React.FunctionComponent<CollateralBasketOverviewPanelProps> = props => {
  const {
    collateralBasket,
    collateralBasketProductSector,
    collateralBasketProduct,
    getProductSectorIdsForType,
    readOnly,
    onUpdate,
    isNewCollateralBasket
  } = props
  const validationIssues = props.validationIssues ?? EMPTY_VALIDATION_ISSUES
  const isGcBespoke = collateralBasketProductSector?.basketType === CollateralBasketType.GC_BESPOKE
  const isDbv = collateralBasketProductSector?.basketType === CollateralBasketType.DBV
  const isCash = collateralBasketProductSector?.basketType === CollateralBasketType.CASH
  const isClearstream = collateralBasketProductSector?.basketType === CollateralBasketType.CLEAR_STREAM
  const isCollateral = collateralBasketProductSector?.basketType === CollateralBasketType.COLLATERAL
  const isEuroclear = collateralBasketProductSector?.basketType === CollateralBasketType.EUROCLEAR
  const isBony = collateralBasketProductSector?.basketType === CollateralBasketType.BONY
  const isUsGcBespoke = isGcBespoke && collateralBasketProduct?.market === Market.US
  const isGcPlus = collateralBasketProductSector?.basketType === CollateralBasketType.GC_PLUS

  const updateHandler = useUpdateHandler(onUpdate)

  const collateralBasketProductSectorIdUpdateHandler = (productSectorId: string) => {
    if (productSectorId !== collateralBasketProductSector?.id) {
      onUpdate({
        productSectorId,
        collateralBasketIsin: null,
        collateralBasketCusip: null,
        collateralIds: []
      })
    }
  }

  const collateralBasketTypeUpdateHandler = (basketType: CollateralBasketType) => {
    if (basketType && basketType !== CollateralBasketType.GC_BESPOKE) {
      onUpdate({
        productSectorId: getProductSectorIdsForType(basketType)[0],
        collateralBasketIsin: null,
        collateralBasketCusip: null,
        collateralIds: [],
        cmeBasketReference: null
      })
    } else if (basketType) {
      onUpdate({
        productSectorId: getProductSectorIdsForType(basketType)[0],
        collateralBasketIsin: null,
        collateralBasketCusip: null,
        collateralIds: []
      })
    }
  }

  const dateUpdateHandler = <K extends keyof Pick<CollateralBasket, 'basketFromDate' | 'basketToDate'>>(key: K) => (
    dateValue: CollateralBasket[K] | null
  ) => {
    onUpdate({ [key]: dateValue })
  }

  const isDynamicBasketUpdateHandler = (isDynamicBasket: boolean) => {
    onUpdate({ isDynamicBasket, collateralIds: [] })
  }

  let endDateMinDate = new Date()

  if (collateralBasket.basketFromDate) {
    const basketFromDate = moment(collateralBasket.basketFromDate)
    if (basketFromDate.isAfter(moment(), 'day')) {
      endDateMinDate = basketFromDate.toDate()
    }
  }

  return (
    <>
      <DialogRow>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralBasketOverviewPanel-TextInputField-BasketName'}
            fieldName="name"
            label={Locale.translate(Translation.TC_BASKET_NAME)}
            value={collateralBasket.name}
            placeholder={Locale.translate(Translation.SC_BASKET_NAME_PLACEHOLDER)}
            onUpdate={updateHandler('name')}
            readOnly={readOnly}
            validationIssues={validationIssues}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralBasketOverviewPanel-TextInputField-BasketDescription'}
            fieldName="description"
            label={Locale.translate(Translation.TC_BASKET_DESCRIPTION)}
            value={collateralBasket.description}
            placeholder={Locale.translate(Translation.SC_BASKET_DESCRIPTION_PLACEHOLDER)}
            onUpdate={updateHandler('description')}
            readOnly={readOnly}
            validationIssues={[...validationIssues, ...getCustomIsinIssues(collateralBasket)]}
          />
        </DialogField>
      </DialogRow>
      {isGcBespoke && (
        <DialogRow>
          <DialogField>
            <TextInputField
              dataTestId={'CollateralBasketOverviewPanel-TextInputField-CmeBasketReference'}
              fieldName="cmeBasketReference"
              label={Locale.translate(Translation.TC_BASKET_CME_BASKET_REFERENCE)}
              value={collateralBasket.cmeBasketReference}
              placeholder={Locale.translate(Translation.SC_CME_BASKET_REFERENCE_PLACEHOLDER)}
              onUpdate={updateHandler('cmeBasketReference')}
              readOnly={readOnly || !isGcBespoke}
              validationIssues={validationIssues || undefined}
              maxLength={constants.cmeBasketReferenceFieldLength}
            />
          </DialogField>
        </DialogRow>
      )}
      <DialogRow>
        <DialogField flex={1}>
          <CollateralBasketTypeSelectboxField
            dataTestId={'CollateralBasketOverviewPanel-CollateralBasketTypeSelectboxField-BasketType'}
            fieldName="type"
            label={Locale.translate(Translation.TC_BASKET_TYPE)}
            value={collateralBasketProductSector?.basketType}
            onUpdate={collateralBasketTypeUpdateHandler}
            readOnly={readOnly || !isNewCollateralBasket}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        {!isDbv && !isGcPlus && !isCollateral && !isCash && (
          <DialogField flex={1}>
            <TradingTypeSelectboxField
              dataTestId={'CollateralBasketOverviewPanel-CollateralBasetTradingTypeSelectboxField-TradingType'}
              fieldName="tradingType"
              label={Locale.translate(Translation.TC_TRADING_TYPE)}
              value={collateralBasket.tradingType}
              placeholder={Locale.translate(Translation.SC_PLEASE_SELECT)}
              onUpdate={updateHandler('tradingType')}
              readOnly={readOnly}
              validationIssues={validationIssues || undefined}
            />
          </DialogField>
        )}
      </DialogRow>
      <DialogRow>
        <DialogField flex={1}>
          <CollateralBasketProductSectorSelectboxField
            dataTestId={'CollateralBasketOverviewPanel-CollateralBasketProductSectorSelectboxField-ProductSectors'}
            fieldName="productSectorId"
            label={Locale.translate(Translation.TC_PRODUCT_SECTORS)}
            value={collateralBasketProductSector?.id}
            onUpdate={collateralBasketProductSectorIdUpdateHandler}
            readOnly={readOnly}
            basketType={collateralBasketProductSector?.basketType || null}
            validationIssues={validationIssues || undefined}
            disabled={!collateralBasketProductSector?.basketType}
          />
        </DialogField>

        <DialogField flex={1}>
          <TextInputField
            dataTestId={'CollateralBasketOverviewPanel-TextInputField-Currency'}
            label={Locale.translate(Translation.TC_CURRENCY)}
            value={collateralBasketProductSector?.basketCurrency}
            onUpdate={noop}
            readOnly={true}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField flex={1}>
          <TextInputField
            dataTestId={'CollateralBasketOverviewPanel-TextInputField-BasketOwner'}
            fieldName="collateralBasketOwner"
            label={Locale.translate(Translation.TC_BASKET_OWNER)}
            value={collateralBasket.collateralBasketOwner}
            onUpdate={updateHandler('collateralBasketOwner')}
            readOnly={readOnly}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        {(isBony || isUsGcBespoke) && (
          <DialogField flex={1}>
            <TextInputField
              dataTestId={'CollateralBasketOverviewPanel-TextInputField-cusip'}
              fieldName="collateralBasketCusip"
              label={Locale.translate(Translation.TC_CUSIP)}
              value={collateralBasket.collateralBasketCusip}
              onUpdate={updateHandler('collateralBasketCusip')}
              readOnly={readOnly}
              validationIssues={validationIssues || undefined}
            />
          </DialogField>
        )}
      </DialogRow>
      {isDbv && (
        <DialogRow>
          <DialogField flex={1}>
            <TextInputField
              dataTestId={'CollateralBasketOverviewPanel-TextInputField-DBVClass'}
              fieldName="dbvClass"
              label={Locale.translate(Translation.TC_DBV_CLASS)}
              value={collateralBasket.dbvClass}
              onUpdate={updateHandler('dbvClass')}
              readOnly={readOnly}
              validationIssues={validationIssues || undefined}
            />
          </DialogField>
        </DialogRow>
      )}
      {(isDbv || isEuroclear || isGcPlus || isClearstream) && (
        <DialogRow>
          <DialogField flex={1}>
            <TextInputField
              dataTestId={'CollateralBasketOverviewPanel-TextInputField-ISIN'}
              fieldName="collateralBasketIsin"
              label={Locale.translate(Translation.TC_ISIN)}
              value={collateralBasket.collateralBasketIsin}
              onUpdate={updateHandler('collateralBasketIsin')}
              readOnly={readOnly}
              autoCapitalise={true}
              validationIssues={validationIssues?.concat(getCustomIsinIssues(collateralBasket)) || undefined}
              maxLength={12}
            />
          </DialogField>
        </DialogRow>
      )}
      <DialogRow>
        {!isBony && isGcBespoke && (
          <DialogField>
            <NumericInputField
              dataTestId={'CollateralBasketOverviewPanel-NumericInputField-MinimumAmount'}
              fieldName="minAmount"
              label={Locale.translate(Translation.TC_MINIMUM_AMOUNT)}
              value={collateralBasket.minAmount}
              onUpdate={updateHandler('minAmount')}
              readOnly={readOnly}
              validationIssues={validationIssues || undefined}
            />
          </DialogField>
        )}
        {isGcBespoke && (
          <DialogField flex={1}>
            <NumericInputWithUnit>
              <NumericInputField
                dataTestId={'CollateralBasketOverviewPanel-NumericInputField-MinimumConcentrationLevel'}
                fieldName="minConcentration"
                label={Locale.translate(Translation.TC_MINIMUM_CONCENTRATION_LEVEL)}
                value={collateralBasket.minConcentration}
                formatOptions={{ decimalShift: 2 }}
                onUpdate={updateHandler('minConcentration')}
                readOnly={readOnly}
                validationIssues={validationIssues || undefined}
              />
              <NumericInputUnit>%</NumericInputUnit>
            </NumericInputWithUnit>
          </DialogField>
        )}
        {isGcBespoke && (
          <DialogField flex={1}>
            <NumericInputWithUnit>
              <NumericInputField
                dataTestId={'CollateralBasketOverviewPanel-NumericInputField-MaximumXConcentrationLevel'}
                fieldName="maxConcentration"
                label={Locale.translate(Translation.TC_MAXIMUM_CONCENTRATION_LEVEL)}
                value={collateralBasket.maxConcentration}
                formatOptions={{ decimalShift: 2 }}
                onUpdate={updateHandler('maxConcentration')}
                readOnly={readOnly}
                validationIssues={validationIssues || undefined}
              />
              <NumericInputUnit>%</NumericInputUnit>
            </NumericInputWithUnit>
          </DialogField>
        )}
      </DialogRow>
      <DialogRow horizontalLayout={constants.HorizontalLayout.SpaceBetween}>
        <DialogField flex={1}>
          <TradingDatePickerField
            dataTestId={'CollateralBasketOverviewPanel-TradingDatePickerField-StartDate'}
            fieldName="basketFromDate"
            label={Locale.translate(Translation.TC_START_DATE)}
            value={collateralBasket.basketFromDate}
            minDate={new Date()}
            currency={collateralBasketProductSector?.basketCurrency}
            onUpdate={dateUpdateHandler('basketFromDate')}
            readOnly={readOnly}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField flex={1}>
          <TradingDatePickerField
            dataTestId={'CollateralBasketOverviewPanel-TradingDatePickerField-EndDate'}
            fieldName="basketToDate"
            label={Locale.translate(Translation.TC_END_DATE)}
            value={collateralBasket.basketToDate}
            minDate={endDateMinDate}
            currency={collateralBasketProductSector?.basketCurrency}
            onUpdate={dateUpdateHandler('basketToDate')}
            readOnly={readOnly}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        {isGcBespoke && (
          <DialogField>
            <ToggleField
              dataTestId={'CollateralBasketOverviewPanel-ToggleField-IsDynamicBasketQuestion'}
              fieldName="isDynamicBasket"
              label={Locale.translate(Translation.TC_IS_DYNAMIC_BASKET_QUESTION)}
              checked={!!collateralBasket.isDynamicBasket}
              onUpdate={isDynamicBasketUpdateHandler}
              readOnly={readOnly}
              labelPosition={constants.LabelPosition.Top}
              validationIssues={validationIssues || undefined}
            />
          </DialogField>
        )}
      </DialogRow>
    </>
  )
}
